import axios, { AxiosInstance } from "axios";
import { PackageType } from "@revenuecat/purchases-js";
import { getStripeOfferings, getStripeProducts } from "@/api/stripe";
import {
  RevenuecatStripePackage,
  RevenuecatPurchasesCustomerInfo,
  CustomerInfo,
  SubscriptionEntitlements,
} from "@/types/subscription";

/**
 * The products endpoint fails the whole batch when Stripe can't return one product. Ask for
 * each product on its own then, so a broken product costs only its own price; whatever still
 * fails comes back without one.
 */
const loadStripeProducts = async (productIds: string[]) => {
  try {
    return await getStripeProducts(productIds);
  } catch (error) {
    console.warn("Stripe products failed to load as a batch; retrying one by one", error);
    const results = await Promise.allSettled(productIds.map((id) => getStripeProducts([id])));
    return results.flatMap((result) => (result.status === "fulfilled" ? result.value : []));
  }
};

export class Purchases {
  config: { apiKey: string; appUserId: string } = {
    apiKey: "",
    appUserId: "",
  };
  axios: AxiosInstance;

  constructor() {}

  configure(apiKey: string, appUserId: string) {
    this.config.apiKey = apiKey;
    this.config.appUserId = appUserId;

    this.axios = axios.create({
      baseURL: "https://api.revenuecat.com/v1",
      headers: {
        Authorization: `Bearer ${apiKey}`,
        "Content-Type": "application/json",
        "X-Platform": "stripe",
        "Accept-Language": localStorage.getItem("language") || "en",
      },
    });

    return this;
  }

  async getOfferings() {
    // Get Stripe offerings directly from our backend API
    const data = await getStripeOfferings(this.config.appUserId);

    // Find the current offering based on current_offering_id
    const currentOffering = data.offerings.find(
      (offering) => offering.identifier === data.current_offering_id,
    );

    if (!currentOffering) {
      throw new Error("Current offering not found");
    }

    // Get product IDs from the packages
    const productIds = currentOffering.packages.map((pkg) => pkg.platform_product_identifier);

    // Fetch full Stripe product details
    const stripeProducts = await loadStripeProducts(productIds);

    const packagesById: { [key: string]: RevenuecatStripePackage } = {};
    const availablePackages: RevenuecatStripePackage[] = [];

    for (let pkg of currentOffering.packages) {
      const stripeProduct = stripeProducts.find(
        (product) => product.id === pkg.platform_product_identifier,
      );

      if (!stripeProduct) {
        // Keep the plan: the offering still names its product, so it can be shown and
        // checked out. Only the price is missing.
        console.warn(`Stripe product not found for package: ${pkg.identifier}`);
      }

      const mappedPkg: RevenuecatStripePackage = {
        identifier: pkg.identifier,
        packageType: PackageType.Custom,
        product: {
          identifier: stripeProduct?.id ?? pkg.platform_product_identifier,
          title: stripeProduct?.name ?? pkg.identifier,
          displayName: stripeProduct?.name ?? pkg.identifier,
          description: stripeProduct?.description ?? null,
          currentPrice: stripeProduct?.price
            ? {
                amount: stripeProduct.price.unit_amount,
                currency: stripeProduct.price.currency,
                formattedPrice: stripeProduct.price.unit_amount_decimal,
              }
            : null,
          normalPeriodDuration: "",
          presentedOfferingIdentifier: currentOffering.identifier,
          defaultSubscriptionOption: {},
          subscriptionOptions: {},
        },
      };

      packagesById[pkg.identifier] = {
        identifier: pkg.identifier,
        packageType: PackageType.Custom,
        product: mappedPkg.product,
      };

      availablePackages.push(mappedPkg);
    }

    const offerings = {
      all: {},
      current: {
        identifier: currentOffering.identifier,
        serverDescription: currentOffering.description,
        metadata: currentOffering.metadata || {},
        packagesById: packagesById,
        availablePackages: availablePackages,
        lifetime: null,
        annual: null,
        sixMonth: null,
        threeMonth: null,
        twoMonth: null,
        monthly: null,
        weekly: null,
      },
    };

    return offerings;
  }

  async getCustomerInfo() {
    const { data } = await this.axios.get<RevenuecatPurchasesCustomerInfo>(
      `/subscribers/${this.config.appUserId}`,
    );

    const activeSubscriptions = new Set<string>();
    const allExpirationDatesByProduct: { [key: string]: Date } = {};
    const allPurchaseDatesByProduct: { [key: string]: Date } = {};
    const entitlements: SubscriptionEntitlements = { all: {}, active: {} };
    const now = Date.now();
    let latestActiveEntitlementKey: string | null = null;
    let latestActiveExpiryTimestamp = Number.NEGATIVE_INFINITY;
    let latestActivePurchaseTimestamp = Number.NEGATIVE_INFINITY;

    for (let entitlementKey in data.subscriber.entitlements) {
      const entitlement = data.subscriber.entitlements[entitlementKey];

      const subscriptionKey = Object.keys(data.subscriber.subscriptions).find(
        (key) => key === entitlement.product_identifier,
      );
      const unSubscribedDate =
        subscriptionKey && data.subscriber.subscriptions[subscriptionKey].unsubscribe_detected_at
          ? data.subscriber.subscriptions[subscriptionKey].unsubscribe_detected_at
          : null;

      entitlements.all[entitlementKey] = {
        store: subscriptionKey ? data.subscriber.subscriptions[subscriptionKey].store : null,
        identifier: entitlementKey,
        productIdentifier: entitlement.product_identifier,
        expirationDate: entitlement.expires_date,
        latestPurchaseDate: entitlement.purchase_date,
        unsubscribeDetectedAt: unSubscribedDate,
      };

      const expiryTimestamp = entitlement.expires_date
        ? new Date(entitlement.expires_date).getTime()
        : Number.POSITIVE_INFINITY;
      const purchaseTimestamp = entitlement.purchase_date
        ? new Date(entitlement.purchase_date).getTime()
        : Number.NEGATIVE_INFINITY;
      const isActiveEntitlement = Number.isFinite(expiryTimestamp) ? expiryTimestamp > now : true;

      if (
        isActiveEntitlement &&
        (expiryTimestamp > latestActiveExpiryTimestamp ||
          (expiryTimestamp === latestActiveExpiryTimestamp &&
            purchaseTimestamp > latestActivePurchaseTimestamp))
      ) {
        latestActiveEntitlementKey = entitlementKey;
        latestActiveExpiryTimestamp = expiryTimestamp;
        latestActivePurchaseTimestamp = purchaseTimestamp;
      }
    }

    if (latestActiveEntitlementKey) {
      // The web UI expects a single winning entitlement for the current Stripe plan.
      entitlements.active[latestActiveEntitlementKey] =
        entitlements.all[latestActiveEntitlementKey];
      activeSubscriptions.add(entitlements.active[latestActiveEntitlementKey].productIdentifier);
    }

    const customerInfo: CustomerInfo = {
      activeSubscriptions: Array.from(activeSubscriptions),
      allExpirationDatesByProduct,
      allPurchaseDatesByProduct,
      firstSeenDate: data.subscriber.first_seen,
      requestDate: data.subscriber.last_seen,
      managementURL: data.subscriber.management_url,
      originalAppUserId: data.subscriber.original_app_user_id,
      originalPurchaseDate: data.subscriber.original_purchase_date,
      entitlements,
    };

    return customerInfo;
  }

  purchase() {}

  async createReceipts(sessionId: string) {
    const { data } = await this.axios.post("/receipts", {
      app_user_id: this.config.appUserId,
      fetch_token: sessionId,
    });

    return data;
  }
}

export default new Purchases();
